/**
 * @architecture-header 1
 * @layer module
 * @domain activities
 * @responsibility Render the upload dialog including answer-attachment drag and drop.
 * @state-owner activities UI store holds the upload draft
 * @allowed-imports React; activities store; dataset and workspace stores; shared components and hooks
 * @forbidden direct API client; student-only surfaces
 * @public-api activities module
 * @side-effects creates activities and queues the parsing simulation
 * @tests workspace Playwright flows in frontend/apps/web/e2e/workspace.spec.ts
 */

import { type DragEvent, useRef, useState } from 'react';
import { ModalDocument, SectionLabel } from '../../../shared/components/Modal';
import { useOverlay } from '../../../shared/components/OverlayHost';
import { MoveOutIcon, SmallCloseIcon } from '../../../shared/components/VispoIcon';
import type { UploadDraftFile } from '../../../shared/lib/model';
import { useDatasetStore } from '../../../shared/model/dataset-store';
import { useTranslator } from '../../../shared/model/use-workspace';
import { useWorkspaceUiStore } from '../../../shared/model/workspace-store';
import { useActivityUiStore } from '../store/activity-ui-store';

/** Newly uploaded material sorts above everything seeded, without needing a clock. */
const NEW_ACTIVITY_ORDINAL = 1000;

export function uploadKind(name: string): 'IMG' | 'DOC' | 'PDF' {
  if (/\.(png|jpe?g|heic|webp)$/i.test(name)) return 'IMG';
  if (/\.docx?$/i.test(name)) return 'DOC';
  return 'PDF';
}

function uploadMeta(name: string): string {
  const kind = uploadKind(name);
  return kind === 'IMG' ? '圖片' : kind === 'DOC' ? 'Word' : 'PDF';
}

interface Located {
  readonly node: UploadDraftFile | { id: string; name: string };
  readonly parent: UploadDraftFile | null;
}

function locate(files: readonly UploadDraftFile[], id: string): Located | null {
  for (const file of files) {
    if (file.id === id) return { node: file, parent: null };
    if (file.answer?.id === id) return { node: file.answer, parent: file };
  }
  return null;
}

/** Remove a node from wherever it currently lives. */
function take(
  files: readonly UploadDraftFile[],
  id: string,
): { files: UploadDraftFile[]; node: UploadDraftFile | null } {
  const found = locate(files, id);
  if (!found) return { files: [...files], node: null };
  if (found.parent) {
    return {
      files: files.map((file) => (file.id === found.parent?.id ? { ...file, answer: null } : file)),
      node: { id: found.node.id, name: found.node.name, answer: null },
    };
  }
  return {
    files: files.filter((file) => file.id !== id),
    node: found.node as UploadDraftFile,
  };
}

export function UploadDialog() {
  const t = useTranslator();
  const { closeModal } = useOverlay();
  const notify = useWorkspaceUiStore((state) => state.notify);
  const go = useWorkspaceUiStore((state) => state.go);
  const classes = useDatasetStore((state) => state.classes);
  const files = useActivityUiStore((state) => state.uploadFiles);
  const classIndex = useActivityUiStore((state) => state.uploadClassIndex);
  const addUploadFiles = useActivityUiStore((state) => state.addUploadFiles);
  const setUploadFiles = useActivityUiStore((state) => state.setUploadFiles);
  const clearUpload = useActivityUiStore((state) => state.clearUpload);
  const [dragging, setDragging] = useState<string | null>(null);
  const [over, setOver] = useState<string | null>(null);
  const [zoneOver, setZoneOver] = useState(false);
  const [dropZoneOver, setDropZoneOver] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const remove = (id: string) => {
    const found = locate(files, id);
    const next = take(files, id);
    setUploadFiles(next.files);
    notify(found?.parent ? '已刪除答案附件' : t('已刪除'));
  };

  const moveOut = (id: string) => {
    const found = locate(files, id);
    if (!found?.parent) return;
    const next = take(files, id);
    if (!next.node) return;
    setUploadFiles([...next.files, { ...next.node, answer: null }]);
    notify(`《${next.node.name}》已移出，變成獨立的考卷`);
  };

  const dropOnto = (targetId: string) => {
    const source = dragging;
    setDragging(null);
    setOver(null);
    if (!source || source === targetId) return;
    const target = locate(files, targetId);
    if (!target) return;
    const paperId = target.parent ? target.parent.id : target.node.id;
    if (paperId === source) return;
    const taken = take(files, source);
    const moved = taken.node;
    if (!moved) return;
    let next = taken.files;
    if (moved.answer) next = [...next, { ...moved.answer, answer: null }];
    const paper = next.find((file) => file.id === paperId);
    if (!paper) return;
    if (paper.answer) next = [...next, { ...paper.answer, answer: null }];
    next = next.map((file) =>
      file.id === paperId ? { ...file, answer: { id: moved.id, name: moved.name } } : file,
    );
    setUploadFiles(next);
    notify(`《${moved.name}》已設成《${paper.name}》的答案`);
  };

  const submit = () => {
    if (!files.length) return;
    const store = useDatasetStore.getState();
    const created = files.map((file, index) => ({
      id: store.allocateActivityId(),
      tags: [] as string[],
      createdAt: NEW_ACTIVITY_ORDINAL + index,
      updatedAt: NEW_ACTIVITY_ORDINAL + index,
      name: file.name.replace(/\.[a-z0-9]+$/i, ''),
      questionCount: 0,
      status: 'parsing' as const,
      parsedPercent: 8,
      classIndex: null,
      updatedLabel: '剛剛',
      reviewed: file.answer ? [1, 0, 0, 1] : [0, 0, 0, 0],
      answerSource: (file.answer ? 'file' : 'ai') as 'file' | 'ai',
      answerFileName: file.answer ? file.answer.name : null,
      usedByClassIndexes: classIndex != null ? [classIndex] : [],
    }));
    store.prependActivities(created);
    clearUpload();
    closeModal();
    go('act', null);
    notify(`${created.length} 份已排入解析`);
  };

  const kindChip = (name: string) => (
    <span className={`ukind k${uploadKind(name)}`}>{uploadKind(name)}</span>
  );

  const onFileDrop = (event: DragEvent<HTMLElement>) => {
    event.preventDefault();
    setZoneOver(false);
    if (dragging) {
      moveOut(dragging);
      setDragging(null);
      return;
    }
    const names = event.dataTransfer?.files?.length
      ? Array.from(event.dataTransfer.files).map((file) => file.name)
      : [t('未命名檔案.pdf')];
    addUploadFiles(names);
  };

  return (
    <ModalDocument
      footer={
        <>
          <div className="sp" />
          <button className="bt" type="button" onClick={closeModal}>
            {t('取消')}
          </button>
          <button className="bt pri" disabled={!files.length} type="button" onClick={submit}>
            {`上傳並解析${files.length ? ` ${files.length} 份` : ''}`}
          </button>
        </>
      }
      maxWidth={620}
      subtitle={`${classIndex != null ? `${classes[classIndex].name} · ` : ''}${t(
        '一次可以丟多份，PDF、Word、圖片都可以。每一份就是一次評量。',
      )}`}
      title={t('上傳活動')}
    >
      {/* biome-ignore lint/a11y/noStaticElementInteractions: drop target enhances the "選擇檔案" button; the button is the accessible path */}
      <div
        className={`xstartwrap${zoneOver ? ' over' : ''}`}
        onDragLeave={() => setZoneOver(false)}
        onDragOver={(event) => {
          event.preventDefault();
          setZoneOver(true);
        }}
        onDrop={onFileDrop}
      >
        <h2 style={{ fontSize: 16 }}>{t('把檔案拖進來')}</h2>
        <p style={{ marginBottom: 14 }}>{t('或從電腦挑幾個')}</p>
        <div style={{ display: 'flex', gap: 8, justifyContent: 'center' }}>
          <button className="bt" type="button" onClick={() => inputRef.current?.click()}>
            {t('選擇檔案')}
          </button>
          <button
            className="bt"
            type="button"
            onClick={() =>
              addUploadFiles([
                t('第 4 章 二元一次方程式.pdf'),
                t('第 4 章 解答.pdf'),
                t('隨堂測驗 5-1.png'),
              ])
            }
          >
            {t('放三個範例')}
          </button>
        </div>
        <input
          accept=".pdf,.doc,.docx,image/*"
          multiple
          onChange={(event) => {
            addUploadFiles(Array.from(event.target.files ?? []).map((file) => file.name));
            event.target.value = '';
          }}
          ref={inputRef}
          style={{ display: 'none' }}
          type="file"
        />
      </div>

      {files.length ? (
        <>
          <SectionLabel>
            {`${t('檔案')} `}
            <span style={{ color: '#9a9aa2', fontWeight: 400 }}>{`${files.length} 份`}</span>
          </SectionLabel>
          <div className="uplist">
            {files.map((file) => (
              // biome-ignore lint/a11y/noStaticElementInteractions: reordering is drag-only; the ↥ and × buttons cover the same operations
              <div
                className={`uprow${dragging === file.id ? ' dragging' : ''}${
                  over === file.id ? ' over' : ''
                }`}
                data-id={file.id}
                draggable
                key={file.id}
                onDragEnd={() => {
                  setDragging(null);
                  setOver(null);
                }}
                onDragLeave={() => setOver(null)}
                onDragOver={(event) => {
                  if (!dragging || dragging === file.id) return;
                  event.preventDefault();
                  setOver(file.id);
                }}
                onDragStart={() => setDragging(file.id)}
                onDrop={(event) => {
                  event.preventDefault();
                  event.stopPropagation();
                  dropOnto(file.id);
                }}
              >
                <span className="ugrip">⠿</span>
                {kindChip(file.name)}
                <span className="ut">
                  <b>{file.name}</b>
                  <span>
                    {`${uploadMeta(file.name)} · ${
                      file.answer
                        ? '題目卷，已附答案'
                        : t('題目卷 · 答案將由 AI 生成，每題標示待確認')
                    }`}
                  </span>
                </span>
                <button
                  className="uib"
                  title={t('刪除這次評量')}
                  type="button"
                  onClick={(event) => {
                    event.stopPropagation();
                    remove(file.id);
                  }}
                >
                  <SmallCloseIcon />
                </button>
                {file.answer ? (
                  // biome-ignore lint/a11y/noStaticElementInteractions: reordering is drag-only; the ↥ and × buttons cover the same operations
                  <div
                    className={`upans${over === file.answer.id ? ' over' : ''}`}
                    data-id={file.answer.id}
                    draggable
                    onDragEnd={() => {
                      setDragging(null);
                      setOver(null);
                    }}
                    onDragLeave={() => setOver(null)}
                    onDragOver={(event) => {
                      if (!dragging || dragging === file.answer?.id) return;
                      event.preventDefault();
                      setOver(file.answer?.id ?? null);
                    }}
                    onDragStart={(event) => {
                      event.stopPropagation();
                      setDragging(file.answer?.id ?? null);
                    }}
                    onDrop={(event) => {
                      event.preventDefault();
                      event.stopPropagation();
                      if (file.answer) dropOnto(file.answer.id);
                    }}
                  >
                    <span className="ugrip">⠿</span>
                    {kindChip(file.answer.name)}
                    <span className="ut">
                      <b>{file.answer.name}</b>
                      <span>{t('答案附件')}</span>
                    </span>
                    <button
                      className="uib"
                      title={t('移出成獨立考卷')}
                      type="button"
                      onClick={(event) => {
                        event.stopPropagation();
                        if (file.answer) moveOut(file.answer.id);
                      }}
                    >
                      <MoveOutIcon />
                    </button>
                    <button
                      className="uib"
                      title={t('刪除答案附件')}
                      type="button"
                      onClick={(event) => {
                        event.stopPropagation();
                        if (file.answer) remove(file.answer.id);
                      }}
                    >
                      <SmallCloseIcon />
                    </button>
                  </div>
                ) : null}
              </div>
            ))}
          </div>
          {/* biome-ignore lint/a11y/noStaticElementInteractions: drop target duplicates the ↥ button on each attachment */}
          <div
            className={`upov${dragging ? ' show' : ''}${dropZoneOver ? ' over' : ''}`}
            id="upov"
            onDragLeave={() => setDropZoneOver(false)}
            onDragOver={(event) => {
              event.preventDefault();
              setDropZoneOver(true);
            }}
            onDrop={(event) => {
              event.preventDefault();
              setDropZoneOver(false);
              if (dragging) moveOut(dragging);
              setDragging(null);
            }}
          >
            拖到這裡移出成獨立的考卷
          </div>
          <p className="uphint">
            把答案檔拖到題目卷上面就變成它的答案；把附件拖出來或按 ↥ 就會變回獨立考卷。裡外都可以按
            × 刪除。 有答案的照答案產生詳解，沒有的由 AI 生成並標成待確認。
          </p>
        </>
      ) : (
        <p className="uphint" style={{ marginTop: 16 }}>
          {t('還沒有檔案。')}
        </p>
      )}
    </ModalDocument>
  );
}
